import React, { useState, useEffect, SyntheticEvent } from 'react'
import { flowRight as compose, isEmpty } from 'lodash'
import { useTranslation } from 'react-i18next'
import type { RouteChildrenProps } from 'react-router-dom'
import { Table, Button } from 'components'
import { THead } from 'components/Table'
import { IconPlus } from 'components/Icons'
import { LIMIT_PAGE } from 'helpers/constants'
import { withBackOfficeCms } from 'helpers/hocs'
import { useBreadcrumbs, useSearchParams, useCancelToken } from 'helpers/hooks'
import { routes } from 'helpers/routes'
import {
  TicketSearchAndFilter,
  TicketList,
  TicketPagination,
  HEADERS,
  findAllTask,
  BREADCRUMBS_LIST,
} from 'features/Ticket'
import { useSetRecoilState } from 'recoil'
import { currentStatusProgressState } from 'recoils/atoms'

import type { IParamTicket } from 'features/Ticket'
import type { ISorting, Ticket } from 'types'
import { getSessionStorage, removeSessionStorage } from 'helpers/common'

const TicketContainer: React.FC<RouteChildrenProps> = ({
  history,
}: RouteChildrenProps) => {
  const defaultSearchParams: IParamTicket = {
    _sort: 'code',
    _order: 'DESC',
    _page: 1,
  }
  const [searchParams, setSearchParams] =
    useSearchParams<IParamTicket>(defaultSearchParams)

  const [totalPages, setTotalPages] = useState<number>(0)
  const [prevPage, setPrevPage] = useState<number>(0)
  const [tasks, setTasks] = useState<Ticket[]>()
  const [loading, setLoading] = useState<boolean>(true)

  const { newCancelToken, isCancel } = useCancelToken()
  const setTagStatus = useSetRecoilState(currentStatusProgressState)
  const { t } = useTranslation(['layout', 'ticket'])

  const pathBeforeCheckConsent = getSessionStorage('pathBeforeCheckConsent')

  const base = 'con-ticket'
  const currentPage: number = Number(searchParams._page) || 1

  const onSortChange = (sort: ISorting): void => {
    setSearchParams({ _sort: sort.column, _order: sort.order, _page: 1 })
  }

  const fetchTask = async (
    searchParams?: IParamTicket,
    option?: {
      isRemoveStatusProgress: boolean
    },
  ) => {
    setSearchParams({ ...searchParams })

    try {
      setLoading(true)
      const { data, count } = await findAllTask(
        searchParams,
        option?.isRemoveStatusProgress,
        {
          cancelToken: newCancelToken(),
        },
      )
      const taskData = data as Ticket[]

      if (Boolean(option?.isRemoveStatusProgress)) {
        setTasks([])
      } else {
        setTasks(taskData)
      }

      if (!isEmpty(taskData)) {
        setLoading(false)
        setTagStatus('')
        setTotalPages(Math.ceil(Number(count) / LIMIT_PAGE) || 1)
      } else {
        setLoading(false)
      }

      setPrevPage(0)
    } catch (e) {
      if (isCancel(e)) return
      setPrevPage(0)
      setLoading(false)
    }
  }

  const handleClickRow = (id: string) => (e: SyntheticEvent) => {
    e.preventDefault()
    history.push(`${routes.ticket.path}/${id}/detail`)
  }

  if (pathBeforeCheckConsent) {
    removeSessionStorage('pathBeforeCheckConsent')

    history.push(pathBeforeCheckConsent)

    return <></>
  }

  useEffect(() => {
    if (prevPage === Number(searchParams._page)) {
      fetchTask(searchParams)
    }
  }, [prevPage])

  useEffect(() => {
    fetchTask(searchParams)
  }, [
    searchParams.q,
    searchParams._page,
    searchParams._sort,
    searchParams._order,
    searchParams['requestDate[gte]'],
    searchParams['requestDate[lte]'],
    searchParams.statusPriority,
    searchParams['type.id'],
  ])

  useBreadcrumbs(BREADCRUMBS_LIST)

  return (
    <div className={base}>
      <TicketSearchAndFilter isLoading={loading} fetchData={fetchTask} />

      <div className='float-button--wrap'>
        <div className='float-button--group'>
          <Button
            className='buttonContent'
            withIcon={<IconPlus variants='sm' />}
            onClick={() => history.push(routes.ticket.branch.path)}
          >
            {t('ticket:add.button')}
          </Button>
        </div>
      </div>

      <div className='row'>
        <Table>
          <THead
            columns={HEADERS}
            onSortChange={onSortChange}
            sorting={{
              column: searchParams._sort as string,
              order: searchParams._order || 'DESC',
            }}
            position='left'
          />
          <TicketList
            columns={HEADERS}
            tickets={tasks || []}
            loading={loading}
            onClickRow={handleClickRow}
          />
        </Table>
      </div>

      <div className={`${base}__con-bottom`}>
        <TicketPagination
          currentPage={currentPage}
          totalPage={totalPages}
          disabled={loading}
          setSearchParams={setSearchParams}
          setPrevPage={setPrevPage}
        />
      </div>
    </div>
  )
}
export default compose(withBackOfficeCms)(TicketContainer)
